import React, { useMemo, memo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import client from '@atlassian/jira-apollo-gira/src/index.tsx';
import { ApolloClientProvider } from '@atlassian/jira-apollo-multiple-clients/src/main.tsx';
import BusinessAppWrapper from '@atlassian/jira-business-app-wrapper/src/ui/app-wrapper/index.tsx';
import { IssueTypesAndFieldsContext } from '@atlassian/jira-business-entity-project/src/controllers/issue-types-and-fields-context/index.tsx';
import { ExperienceErrorBoundary } from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { IssueCreateProvider } from '@atlassian/jira-business-issue-create/src/controllers/issue-create-context/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import type { main_businessTimeline_TimelineViewRoot$key } from '@atlassian/jira-relay/src/__generated__/main_businessTimeline_TimelineViewRoot.graphql';
import { MAIN_EXPERIENCE } from './common/constants.tsx';
import { useTimelineFeatures } from './controllers/features-context/index.tsx';
import { useTimelineUI } from './controllers/timeline-ui/index.tsx';
import { useCreateIssue } from './services/create-issue/index.tsx';
import TimelineView from './ui/timeline-view/index.tsx';

const analyticsSourceName = 'timelineView';

export const TimelineViewRoot = memo(
	({ queryFragment }: { queryFragment: main_businessTimeline_TimelineViewRoot$key }) => {
		const { hasInlineCreate, hasFilterCustomFields } = useTimelineFeatures();
		const [{ timelineMode }] = useTimelineUI();
		const analyticsAttributes = useMemo(
			() => ({
				timelineMode,
			}),
			[timelineMode],
		);
		const services = useCreateIssue();

		const data = useFragment(
			graphql`
				fragment main_businessTimeline_TimelineViewRoot on Query {
					...issueCreateContext_businessIssueCreate_IssueCreateProvider
				}
			`,
			queryFragment,
		);

		const content = (
			<ContextualAnalyticsData
				objectType="timeline"
				attributes={analyticsAttributes}
				sourceType={SCREEN}
				sourceName={analyticsSourceName}
			>
				<TimelineView />
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		);

		const isUsingIssueTypesAndFields = hasInlineCreate || hasFilterCustomFields;

		// Temporary placeholder which will be removed when cleaning up isVisualRefreshEnabled
		const content2 = (
			<>
				{isUsingIssueTypesAndFields ? (
					<IssueTypesAndFieldsContext>
						{hasInlineCreate ? (
							<IssueCreateProvider
								onCreateIssue={services.createIssue}
								onRefetchIssue={services.refetchIssue}
								queryFragment={data}
							>
								{content}
							</IssueCreateProvider>
						) : (
							content
						)}
					</IssueTypesAndFieldsContext>
				) : (
					content
				)}
			</>
		);

		return (
			<BusinessAppWrapper withSidebar withOverflow shouldAddPadding={!isVisualRefreshEnabled()}>
				{isVisualRefreshEnabled() ? <Box xcss={timelineStyles}>{content2}</Box> : content2}
			</BusinessAppWrapper>
		);
	},
);

const Timeline = ({
	queryFragment,
}: {
	queryFragment: main_businessTimeline_TimelineViewRoot$key;
}) => (
	<ExperienceErrorBoundary experience={MAIN_EXPERIENCE} shouldStartExperience>
		<ApolloClientProvider client={client}>
			<TimelineViewRoot queryFragment={queryFragment} />
		</ApolloClientProvider>
	</ExperienceErrorBoundary>
);

export default memo(Timeline);

const timelineStyles = xcss({
	overflowX: 'hidden',
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	flexShrink: 1,
	maxWidth: '100%',
	minWidth: '0',
	width: 'auto',
	paddingLeft: 'space.300',
});
